/** TTL for place provider data (Google Places). Both client and server compare against this. */
export const PLACE_STALE_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Stored hotel photo sets older than this are refetched on the next surface — galleries track
 * provider updates without a backfill job, and "no media" outcomes get periodically re-checked.
 *
 * 7 days (was 30): Sabre content-hashes its CDN filenames and rotates them when a property's
 * photo set is re-uploaded, 404ing every stored URL — observed 2026-08-20 across hotels from a
 * single backfill batch. The window bounds how long a rotated set renders broken images, at the
 * cost of a billed provider call per actively-viewed hotel per week (cheap at our volumes).
 *
 * SERVER-ONLY runtime value, not a client contract: only `ensureHotelPhotos` reads it (photos
 * are fetched and staleness-checked entirely server-side). It lives here purely to sit beside
 * the other media/pricing TTLs — unlike `PLACE_STALE_MS`, the client never compares against it.
 */
export const HOTEL_PHOTOS_STALE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Age after which a stored flight/room price is considered worth re-checking.
 *
 * Shared (not re-derived) so every client — web today, native tomorrow — uses the same
 * window when deciding to surface a "recheck price" affordance. Pairs with the offline
 * `isPriceStale` predicate, which compares `now() - fetchedAt` against this threshold.
 *
 * NOTE: price staleness is a **compound, read-time** predicate, not a stored flag:
 * a price is worth rechecking only when it is older than this threshold AND the travel
 * date has not already passed (a departed trip never needs a price recheck). The boolean
 * is intentionally **not** denormalized onto the document — it would be wrong the moment
 * the clock advances past the threshold. Compute it on read against `now()`.
 */
export const STALE_THRESHOLD_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Maximum total byte size for message attachments on a single message.
 *
 * Enforced in two places that must stay in sync:
 * - Client-side as inline UX (`ChatInputState`) so users get immediate feedback.
 * - Server-side as a hard security boundary (`SendMessageFlow`) so direct flow
 *   callers — or native clients that omit the UX check — cannot push unbounded payloads.
 */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25MB

/**
 * Public membership application page (served by the separate `elara-landing` site). The landing
 * site stores `utm_*` and `ref` on the waitlist submission, so links carry them for attribution.
 */
export const MEMBERSHIP_APPLICATION_URL = 'https://elara.travel/apply';

/**
 * Builds a membership application link with campaign attribution and an optional referral code.
 * The referral code credits the referring member and fast-tracks the applicant.
 *
 * @example
 * buildMembershipApplicationUrl({ source: 'shared-itinerary', medium: 'share-link', ref: 'ABC123' });
 * // 'https://elara.travel/apply?utm_source=shared-itinerary&utm_medium=share-link&ref=ABC123'
 */
export function buildMembershipApplicationUrl(params: AttributionParams): string {
  return withAttribution(MEMBERSHIP_APPLICATION_URL, params);
}

/** The public marketing site's home page (the `elara-landing` site), for "learn more" links. */
export const MARKETING_SITE_URL = 'https://elara.travel/';

/**
 * Builds a marketing-site link with the same attribution as {@link buildMembershipApplicationUrl}.
 * The landing site keeps `utm_*` for the session, so a visitor who reads on and applies later is
 * still credited to the campaign.
 */
export function buildMarketingSiteUrl(params: AttributionParams): string {
  return withAttribution(MARKETING_SITE_URL, params);
}

interface AttributionParams {
  source: string;
  medium: string;
  campaign?: string;
  content?: string;
  ref?: string;
}

function withAttribution(base: string, params: AttributionParams): string {
  const url = new URL(base);
  url.searchParams.set('utm_source', params.source);
  url.searchParams.set('utm_medium', params.medium);
  if (params.campaign) url.searchParams.set('utm_campaign', params.campaign);
  if (params.content) url.searchParams.set('utm_content', params.content);
  if (params.ref) url.searchParams.set('ref', params.ref);
  return url.toString();
}

/**
 * The `details.code` on the FAILED_PRECONDITION a room add gets when the hotel has no more of that room
 * for the trip's dates. The server sets it and the client reads it to show the hotel's answer rather than
 * a generic failure, so both import it from here and cannot drift apart.
 */
export const INSUFFICIENT_INVENTORY_CODE = 'insufficient_inventory';

/**
 * Server path of Elara's contact card (vCard), opened by "Add Elara to Contacts" (ELARA-1178).
 * Shared because the server serves it and the web and mobile apps open it.
 */
export const ELARA_CONTACT_VCARD_PATH = '/api/elara-contact.vcf';

/**
 * The Elara iPhone app on the App Store (the `travel.elara.app` record, Apple ID `6790430556`).
 * The default for the force-update gate's store link and the link in the SMS welcome text.
 */
export const ELARA_IOS_APP_STORE_URL = 'https://apps.apple.com/app/id6790430556';
